// PreQualRefi.js

import { frequencyToMonthly } from "./formUtils";

export default function calculateRefinancePreQual(formData) {
  const toNum = (v) =>
    parseFloat((v || "").toString().replace(/[^0-9.]/g, "")) || 0;

  const fhaLimits = {
    CULEBRA: 532450,
    defaultLow: 524225,
    defaultHigh: 690000,
  };
  const normalize = (str) => str.normalize("NFD").replace(/[̀-ͯ]/g, "");
  const location = normalize((formData.location || "").trim().toUpperCase());
  let fhaMaxLoanAmount = fhaLimits.defaultLow;
  const highLimitCounties = [
    "AGUAS BUENAS",
    "AIBONITO",
    "BARCELONETA",
    "BARRANQUITAS",
    "BAYAMON",
    "CAGUAS",
    "CANOVANAS",
    "CAROLINA",
    "CATANO",
    "CAYEY",
    "CEIBA",
    "CIALES",
    "CIDRA",
    "COMERIO",
    "COROZAL",
    "DORADO",
    "FAJARDO",
    "FLORIDA",
    "GUAYNABO",
    "GURABO",
    "HUMACAO",
    "JUNCOS",
    "LAS PIEDRAS",
    "LOIZA",
    "LUQUILLO",
    "MANATI",
    "MAUNABO",
    "MOROVIS",
    "NAGUABO",
    "NARANJITO",
    "OROCOVIS",
    "RIO GRANDE",
    "SAN JUAN",
    "SAN LORENZO",
    "TOA ALTA",
    "TOA BAJA",
    "TRUJILLO ALTO",
    "UTUADO",
    "VEGA ALTA",
    "VEGA BAJA",
    "YABUCOA",
  ];
  if (location === "CULEBRA") {
    fhaMaxLoanAmount = fhaLimits["CULEBRA"];
  } else if (highLimitCounties.includes(location)) {
    fhaMaxLoanAmount = fhaLimits.defaultHigh;
  }

  // 1. Monthly Income
  const monthlyIncome =
  frequencyToMonthly(
    toNum(formData.income),
    formData.incomeFrequency,
    toNum(formData.hoursPerWeek)
  ) +
  frequencyToMonthly(
    toNum(formData.additionalWorkIncome),
    formData.additionalWorkIncomeFrequency,
    toNum(formData.additionalWorkHoursPerWeek)
  ) +
  frequencyToMonthly(
    toNum(formData.bonusIncome),
    formData.bonusIncomeFrequency
  ) +
  frequencyToMonthly(
    toNum(formData.spouseIncome),
    formData.spouseIncomeFrequency,
    toNum(formData.spouseHoursPerWeek)
  ) +
  frequencyToMonthly(
    toNum(formData.spouseAdditionalWorkIncome),
    formData.spouseAdditionalWorkIncomeFrequency,
    toNum(formData.spouseAdditionalWorkHoursPerWeek)
  ) +
  frequencyToMonthly(
    toNum(formData.spouseBonusIncome),
    formData.spouseBonusIncomeFrequency
  );

  // 2. Monthly Debts NOT being consolidated
  const consolidatedFields = Array.isArray(formData.consolidatedFields)
    ? formData.consolidatedFields
    : [];
  const consolidatedSet = new Set(
    consolidatedFields.map((f) => f.toLowerCase())
  );
  const monthlyToBalanceMap = {
    AutoLoanMonthly: "AutoLoanBalance",
    spouseAutoLoanMonthly: "spouseAutoLoanBalance",
    personalLoanMonthly: "personalLoanBalance",
    spousePersonalLoanMonthly: "spousePersonalLoanBalance",
    studentLoanMonthly: "studentLoanBalance",
    spouseStudentLoanMonthly: "spouseStudentLoanBalance",
    CreditCardMonthly: "creditCardBalance",
    CreditCard2Monthly: "creditCard2Balance",
    storeCardsMonthly: "storeCardsBalance",
    spouseStoreCardsMonthly: "spouseStoreCardsBalance",
    furnitureLoansMonthly: "furnitureLoansBalance",
    spouseFurnitureLoansMonthly: "spouseFurnitureLoansBalance",
    otherDebtsMonthly: "otherDebtsBalance",
    spouseOtherDebtsMonthly: "spouseOtherDebtsBalance",
  };
  let otherMonthlyDebts = 0;
  for (const [monthlyField, balanceField] of Object.entries(
    monthlyToBalanceMap
  )) {
    if (!consolidatedSet.has((balanceField || "").toLowerCase())) {
      otherMonthlyDebts += toNum(formData[monthlyField]);
    }
  }

  // 3. LTV Cap (NOTE: UFMIP must be included in the cap)
  const includesNonMortgageDebts = consolidatedFields.some(
    (f) => f.toLowerCase() !== "mortgagebalance"
  );
  const ltvCap = includesNonMortgageDebts ? 0.8 : 0.965;
  const propertyValue = toNum(formData.propertyValue);
  const totalNoteCap = Math.min(propertyValue * ltvCap, fhaMaxLoanAmount);
  const baseLoanMax = totalNoteCap / 1.0175;

  // 4. Loan math
  const creditScore =
    (formData.creditScore || "") === "750+" ||
    (formData.spouseCreditScore || "") === "750+"
      ? 750
      : 700;
  const rate = creditScore >= 750 ? 0.0625 : 0.065;
  const monthlyRate = rate / 12;
  const nper = 30 * 12;

  function calcPmt(principal, monthlyRate, nper) {
    if (monthlyRate === 0) return principal / nper;
    const pow = Math.pow(1 + monthlyRate, nper);
    return (principal * monthlyRate * pow) / (pow - 1);
  }

  // 5. Calculate max payment allowed by ratios
  // Tiered: 37% if note<=250k, else 32%
  let maxFrontEnd = monthlyIncome * 0.365;
  let usedFrontEndRatio = 0.365;
  let noteForFront = totalNoteCap;
  if (noteForFront > 250000) {
    maxFrontEnd = monthlyIncome * 0.32;
    usedFrontEndRatio = 0.32;
  }
  // Back-end: 47%
  let maxBackEnd = monthlyIncome * 0.465 - otherMonthlyDebts;

  // 6. Now calculate payment for full cap, see if it fits
  const upFrontMI = baseLoanMax * 0.0175;
  const totalNote = baseLoanMax + upFrontMI;
  const pAndI = calcPmt(totalNote, monthlyRate, nper);
  const hazardRate = 0.00298;
  const hazard = (totalNote * hazardRate) / 12;
  const mi = (totalNote * 0.005) / 12;
  const totalPayment = pAndI + hazard + mi;
  const frontEndRatio = totalPayment / monthlyIncome;
  const backEndRatio = (totalPayment + otherMonthlyDebts) / monthlyIncome;

  let qualifiedBaseLoan = 0;
  let qualifiedNote = 0;
  let qualifiedPayment = 0;
  let qualifiedUpFrontMI = 0;

  // 7. Now, if the payment fits BOTH ratios, use the full cap
  if (
    totalPayment <= maxFrontEnd &&
    totalPayment + otherMonthlyDebts <= maxBackEnd
  ) {
    qualifiedBaseLoan = baseLoanMax;
    qualifiedUpFrontMI = upFrontMI;
    qualifiedNote = totalNote;
    qualifiedPayment = totalPayment;
  } else {
    // If NOT, do binary search up to the base loan cap
    let low = 0,
      high = baseLoanMax,
      best = 0,
      bestNote = 0,
      bestPayment = 0,
      bestUpFrontMI = 0;
    for (let i = 0; i < 30; i++) {
      const mid = (low + high) / 2;
      const ufmip = mid * 0.0175;
      const note = mid + ufmip;
      const pay =
        calcPmt(note, monthlyRate, nper) +
        (note * 0.00298) / 12 +
        (note * 0.005) / 12;
      const front = pay / monthlyIncome;
      const back = (pay + otherMonthlyDebts) / monthlyIncome;
      let allowed =
        (note <= 250000 && front <= 0.365) || (note > 250000 && front <= 0.315);
      allowed = allowed && back <= 0.465;
      if (allowed) {
        best = mid;
        bestNote = note;
        bestPayment = pay;
        bestUpFrontMI = ufmip;
        low = mid;
      } else {
        high = mid;
      }
    }
    qualifiedBaseLoan = best;
    qualifiedUpFrontMI = bestUpFrontMI;
    qualifiedNote = bestNote;
    qualifiedPayment = bestPayment;
  }

  const qualifiedHazard = (qualifiedNote * 0.00298) / 12;
  const qualifiedMI = (qualifiedNote * 0.005) / 12;
  const qualifiedPandI = calcPmt(qualifiedNote, monthlyRate, nper);
  const qualifiedFrontEndRatio = qualifiedPayment / monthlyIncome;
  const qualifiedBackEndRatio =
    (qualifiedPayment + otherMonthlyDebts) / monthlyIncome;

  // 8. Officer triggers
  let needsOfficerReview = false,
    officerReason = "";
  if (formData.hasBankruptcy === "Yes") {
    needsOfficerReview = true;
    officerReason = "Client declared bankruptcy";
  } else if (
    formData.hasTaxReturns === "No" &&
    formData.employmentType !== "Retired"
  ) {
    needsOfficerReview = true;
    officerReason = "No tax returns filed";
  } else if (
    formData.employmentType !== "Retired" &&
    (formData.workExperienceDuration === "No" ||
      formData.spouseWorkExperienceDuration === "No")
  ) {
    needsOfficerReview = true;
    officerReason = "Less than 2 years work experience";
  } else if (
    (qualifiedNote <= 250000 && qualifiedFrontEndRatio > 0.365) ||
    (qualifiedNote > 250000 && qualifiedFrontEndRatio > 0.315) ||
    qualifiedBackEndRatio > 0.465
  ) {
    needsOfficerReview = true;
    officerReason = "Does not meet qualifying ratios";
  }

  // 10. Return
  const isEligible = !needsOfficerReview;
  return {
    pricing: {
      FHA: isEligible ? Math.round(qualifiedNote) : 0,
      VA: 0,
      USDA: 0,
    },
    eligibleProducts: isEligible ? ["FHA"] : [],
    qualifiedPayment: isEligible ? qualifiedPayment : 0,
    frontEndRatio: qualifiedFrontEndRatio,
    backEndRatio: qualifiedBackEndRatio,
    ratioLimits: {
      front: usedFrontEndRatio,
      back: 0.47,
    },
    ltvCap,
    propertyValue,
    totalLoanWithUFMIP: qualifiedNote,
    principalAndInterest: qualifiedPandI,
    hazardInsurance: qualifiedHazard,
    monthlyMI: qualifiedMI,
    otherMonthlyDebts,
    needsOfficerReview,
    officerReason,
    usedRate: rate,
    usedTermMonths: nper,
    upFrontMI: qualifiedUpFrontMI,
  };
}
